import type { MessageKey } from '../i18n/messages.ts';

/**
 * The three Milhar tiers.
 *
 * Each is its own game with its own draw cadence, digit count, ticket price and — the
 * point of the ladder — its own accumulating jackpot. Pulling more players into the
 * 4-digit tier does not grow its pool; a headline prize needs a *harder* draw, which is
 * what the 5- and 6-digit tiers are for (doc/MILHAR_TIERS_AND_GROWTH.md §0.1).
 *
 * The 4-digit and hourly 5-digit tiers are live. The daily 6-digit tier remains
 * declared for the lobby ladder but unavailable until its distinct rules are built.
 */
export type MilharTierId = '4' | '5' | '6';

export type MilharTier = {
  id: MilharTierId;
  /** Winning digits. Also the tier's identity in the payout ladder. */
  digits: number;
  /** i18n key for the tier name ("Milhar", "Milhar Hora", "Milhar Dia"). */
  titleKey: MessageKey;
  /** i18n key for the draw cadence ("every 5 min", "hourly", "daily"). */
  cadenceKey: MessageKey;
  /**
   * Minutes between draws.
   *
   * <p>Lets a rounds count be read as elapsed time — "102 rounds accumulating"
   * means nothing to a player, "4 days without a jackpot" does. Rounds × cadence
   * is a **lower bound** on the real elapsed time (a paused or skipped round only
   * makes the true gap longer), so a floored day count is always a safe claim.
   */
  cadenceMinutes: number;
  /**
   * i18n key for the cadence sentence in the rules ("a number comes out every hour").
   *
   * Same reason the payout table is computed from config rather than written out: a
   * cadence baked into the rules copy keeps claiming "every 5 minutes" on the tiers
   * that draw hourly and daily, and the player who waits five minutes for a result
   * does not conclude they misread it (HowItWorks.tsx file header).
   */
  drawPhraseKey: MessageKey;
  /** Whether this tier should appear in the player lobby. */
  visible: boolean;
  /**
   * Whether the backend actually serves this tier.
   *
   * False means there is no pool, round cadence or config for it. An unavailable tier
   * must show **no amount at all**: borrowing a live tier's pool would present the wrong
   * prize as though it belonged to this game.
   */
  available: boolean;
};

export const MILHAR_TIERS: readonly MilharTier[] = [
  { id: '4', digits: 4, titleKey: 'milharTitle', cadenceKey: 'milharTier4Cadence',
    drawPhraseKey: 'milharHowDraw4', cadenceMinutes: 5, visible: true, available: true },
  { id: '5', digits: 5, titleKey: 'milharTier5Title', cadenceKey: 'milharTier5Cadence',
    drawPhraseKey: 'milharHowDraw5', cadenceMinutes: 60, visible: true, available: true },
  { id: '6', digits: 6, titleKey: 'milharTier6Title', cadenceKey: 'milharTier6Cadence',
    drawPhraseKey: 'milharHowDraw6', cadenceMinutes: 1440, visible: false, available: false },
];

/** The tier a player can actually open right now. */
export function liveMilharTiers(): MilharTier[] {
  return MILHAR_TIERS.filter((tier) => tier.visible && tier.available);
}

export function upcomingMilharTiers(): MilharTier[] {
  return MILHAR_TIERS.filter((tier) => tier.visible && !tier.available);
}
